import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  alpha,
  Zoom,
  Fade
} from "@mui/material";
import { ViewModule, ViewList } from '@mui/icons-material';
import { Link } from "react-router-dom";
import FooterManual from "./footerManual";
import Notes from "./header";

const AllSeeModels = () => {
  const { brandName } = useParams();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [hoveredCard, setHoveredCard] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(
          `https://car.amar.co.jp/api/dashboard/product/get/unique/models/by-brand-name?brandName=${encodeURIComponent(brandName)}`
        );

        if (response.status === 204) {
          setModels([]);
          setError("No model found for this Brand");
          return;
        }

        if (!response.ok) {
          throw new Error("Failed to fetch models");
        }

        const data = await response.json();

        if (data.data && Array.isArray(data.data)) {
          const modelNames = data.data.map((model) => model.carModelName);
          modelNames.sort();
          setModels(modelNames);
        } else {
          throw new Error("Unexpected API response format");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, [brandName]);

  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'list' ? 'grid' : 'list');
  };

  // CSS Grid Approach for 8 items per row
  const renderGridView = () => (
    <Box sx={{
      padding: '0 20px',
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(3, 1fr)',
        md: 'repeat(4, 1fr)',
        lg: 'repeat(8, 1fr)'
      },
      gap: 2
    }}>
      {models.map((model, index) => (
        <Zoom in timeout={200 + (index * 10)} key={index}>
          <Card
            sx={{
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: 'white',
              border: '1.5px solid rgba(233, 36, 42, 0.1)',
              borderRadius: "12px",
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #e9242a, #1e3a8a)',
                transform: 'scaleX(0)',
                transition: 'transform 0.3s ease'
              },
              '&:hover': {
                transform: "translateY(-4px)",
                boxShadow: '0 8px 25px rgba(233, 36, 42, 0.15)',
                border: '1.5px solid rgba(233, 36, 42, 0.3)',
                '&::before': {
                  transform: 'scaleX(1)'
                },
                '& .model-name-grid': {
                  color: '#e9242a',
                  transform: 'scale(1.05)'
                }
              }
            }}
            onMouseEnter={() => setHoveredCard(index)}
            onMouseLeave={() => setHoveredCard(null)}
            onClick={() => navigate(`/modelNameByGlobalSearch/${model}`)}
          >
            <CardContent sx={{
              textAlign: "center",
              padding: '12px !important',
              width: '100%'
            }}>
              <Typography
                className="model-name-grid"
                sx={{
                  fontWeight: "600",
                  fontSize: "14px",
                  color: "#000000",
                  transition: 'all 0.3s ease',
                  lineHeight: 1.2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {model}
              </Typography>
            </CardContent>
          </Card>
        </Zoom>
      ))}
    </Box>
  );

  // Updated List View - Only fix separation lines, keep everything else as is
  const renderListView = () => {
    const columns = [[], [], [], [], [], [], [], []];

    models.forEach((model, index) => {
      columns[index % 8].push(model);
    });

    return (
      <Box sx={{
        display: 'flex',
        gap: 2,
        padding: '0 20px',
        flexWrap: 'wrap',
        justifyContent: 'space-between'
      }}>
        {columns.map((columnModels, columnIndex) => (
          <Box
            key={columnIndex}
            sx={{
              flex: '1 1 12%',
              minWidth: {
                xs: '100%',
                sm: '48%',
                md: '32%',
                lg: '23%',
                xl: '12%'
              },
              maxWidth: {
                xs: '100%',
                sm: '48%',
                md: '32%',
                lg: '23%',
                xl: '12%'
              },
              position: 'relative',
              // Only show border if this column has models AND the next column also has models
              '&:not(:last-child)::after': {
                content: '""',
                position: 'absolute',
                top: '10%',
                right: 0,
                bottom: '10%',
                width: '1px',
                background: 'linear-gradient(to bottom, transparent, rgba(233, 36, 42, 0.3), transparent)',
                opacity: columns[columnIndex + 1] && columns[columnIndex + 1].length > 0 ? 0.7 : 0,
                transition: 'opacity 0.3s ease'
              }
            }}
          >
            <List sx={{ padding: 0 }}>
              {columnModels.map((model, index) => (
                <ListItem
                  key={index}
                  sx={{
                    padding: '8px 12px',
                    marginBottom: '2px',
                    borderRadius: '6px',
                    background: 'transparent',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      background: 'rgba(233, 36, 42, 0.05)',
                      transform: 'translateX(4px)',
                      '& .model-name-list': {
                        color: '#e9242a',
                        fontWeight: '600'
                      },
                      '&::before': {
                        width: '3px'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '0px',
                      height: '60%',
                      background: 'linear-gradient(180deg, #e9242a, #1e3a8a)',
                      borderRadius: '0 2px 2px 0',
                      transition: 'all 0.2s ease'
                    }
                  }}
                  onClick={() => navigate(`/modelNameByGlobalSearch/${model}`)}
                >
                  <ListItemText
                    primary={model}
                    primaryTypographyProps={{
                      className: 'model-name-list',
                      sx: {
                        fontWeight: "500",
                        fontSize: "13px",
                        color: "#000000",
                        transition: 'all 0.2s ease',
                        lineHeight: 1.3
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>
    );
  };


  return (
    <Box className="container-main-AllSeeModels" sx={{ background: 'white' }}>
      <Notes />

      {/* Original Background Section */}
      <Box
        className="image-container-AllSeeModels"
        sx={{
          position: "relative",
          height: "50vh",
          backgroundColor: "black",
          overflow: 'hidden'
        }}
      >
        <Box
          component="img"
          src="/image/AllSeeModelsBackgroungCar.png"
          alt="background"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.4
          }}
        />

        {/* Original Breadcrumb Navigation */}
        <Box sx={{
          position: "absolute",
          top: "10%",
          left: "5%",
          color: "white"
        }}>
          <Link to="https://car.amar.co.jp/" style={{ fontSize: "14px", color: "white", textDecoration: "none" }}>
            Home
          </Link>
          <span style={{ margin: '0 8px' }}>/</span>
          <Link to="https://car.amar.co.jp/brand" style={{ fontSize: "14px", color: "white", textDecoration: "none" }}>
            All Brands
          </Link>
          <span style={{ margin: '0 8px' }}>/</span>
          <span style={{ fontSize: "14px", color: "#e9242a", fontWeight: '600' }}>{brandName}</span>
        </Box>

        {/* Original Brand Name Heading */}
        <Typography
          className="support-paragraph-AllSeeModels"
          sx={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: "translateX(-50%)",
            color: "white",
            fontSize: { xs: "24px", md: "36px" },
            fontFamily: "Roboto",
            fontWeight: "800",
            textAlign: 'center'
          }}
        >
          {brandName}
        </Typography>
      </Box>

      {/* ORIGINAL Models Container - Keeping exact same styling */}
      <Box
        className="logo-section-AllSeeModels"
        sx={{
          width: "90%",
          backgroundColor: "#FFFFFF",
          border: "2px solid #EBEBEB",
          position: "relative",
          borderRadius: "20px",
          padding: "30px",
          margin: "20px auto",
          marginBottom: "5%",
          marginTop: "-5%",
          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)'
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <Typography sx={{
            fontWeight: "800",
            fontSize: { xs: "20px", md: "28px" },
            color: "#000000"
          }}>
            All {brandName} Models
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <IconButton
              onClick={() => setViewMode('grid')}
              sx={{
                backgroundColor: viewMode === 'grid' ? '#e9242a' : 'transparent',
                color: viewMode === 'grid' ? 'white' : '#e9242a',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: viewMode === 'grid' ? '#d42026' : 'rgba(233, 36, 42, 0.2)'
                }
              }}
            >
              <ViewModule />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('list')}
              sx={{
                backgroundColor: viewMode === 'list' ? '#e9242a' : 'transparent',
                color: viewMode === 'list' ? 'white' : '#e9242a',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: viewMode === 'list' ? '#d42026' : 'rgba(233, 36, 42, 0.2)'
                }
              }}
            >
              <ViewList />
            </IconButton>
          </Box>
        </Box>

        {/* Loading and Error States */}
        {loading ? (
          <Box sx={{ textAlign: 'center', padding: '40px' }}>
            <Typography sx={{ fontWeight: "600", fontSize: "18px", color: "#1e3a8a" }}>
              Loading models...
            </Typography>
          </Box>
        ) : error ? (
          <Typography sx={{ fontWeight: "600", fontSize: "16px", color: "#e9242a", textAlign: 'center', padding: '20px' }}>
            {error}
          </Typography>
        ) : models.length === 0 ? (
          <Typography sx={{ fontWeight: "600", fontSize: "16px", color: "#e9242a", textAlign: 'center', padding: '20px' }}>
            No model found for this Brand
          </Typography>
        ) : (
          viewMode === 'grid' ? renderGridView() : renderListView()
        )}
      </Box>

      <FooterManual />
    </Box>
  );
};

export default AllSeeModels;